/**
 * ResearchToggleButtonView component
 * Pure UI component for displaying a toggle button for research mode in the chat interface.
 * Renders a button with an icon and text that changes based on the research mode state.
 */

import { Search } from "lucide-react-native";
import React from "react";

import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

/**
 * Props for the ResearchToggleButtonView component
 */
export interface ResearchToggleButtonViewProps {
  /** Whether research mode is currently active */
  isResearchMode: boolean;
  /** Whether the UI is in dark mode */
  isDark: boolean;
  /** Callback function when the button is pressed */
  onToggle: () => void;
}

/**
 * Pure view component for the research toggle button
 * Displays a button that shows current research mode state and allows toggling
 * @param props - Component props
 * @param props.isResearchMode - Whether research mode is currently active
 * @param props.isDark - Whether the UI is in dark mode
 * @param props.onToggle - Callback function when the button is pressed
 */
const ResearchToggleButtonView = ({
  isResearchMode,
  isDark,
  onToggle,
}: ResearchToggleButtonViewProps) => (
  // SE-6800b: active dark:bg-blue-600/light:bg-blue-500 collapsed to
  // bg-accent-primary — a research-mode toggle is a selection/interaction
  // accent (brief rule 2), which the tool's numeric winner already matched
  // (worst=59, Δlight=[-59,-8,9], Δdark=[-27,33,20]). inactive
  // dark:bg-gray-700/light:bg-gray-200 collapsed to bg-surface-subtle
  // (worst=17, Δlight=[13,10,6], Δdark=[10,-1,-17]).
  <Pressable
    onPress={onToggle}
    className={`flex-row items-center justify-center gap-1 rounded-sm px-3 py-2 ${
      isResearchMode ? "bg-accent-primary" : "bg-surface-subtle"
    }`}
    accessibilityLabel="Toggle research mode"
    accessibilityState={{ selected: isResearchMode }}
  >
    <Icon
      as={Search}
      size="md"
      stroke={isResearchMode ? "#fff" : isDark ? "#999" : "#666"}
    />
    {/* SE-6800b: inactive dark:text-gray-300/light:text-gray-700 collapsed
        to text-content-secondary (worst=27, Δlight=[27,17,1], Δdark=[10,6,1]). */}
    <Text
      variant="body-strong"
      className={`${
        isResearchMode ? "text-content-on-accent" : "text-content-secondary"
      }`}
    >
      Research {isResearchMode ? "ON" : "OFF"}
    </Text>
  </Pressable>
);

ResearchToggleButtonView.displayName = "ResearchToggleButtonView";

export default React.memo(ResearchToggleButtonView);
